import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ChevronRight, Shield } from "lucide-react";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";

interface RelatedAuditsSectionProps {
  documentId: Id<"documents">;
}

export function RelatedAuditsSection({
  documentId,
}: RelatedAuditsSectionProps) {
  const { data: relatedAudits } = useWorkspaceQuery(
    api.document_queries.getRelatedAudits,
    { documentId },
  );
  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });

  // Loading state
  if (relatedAudits === undefined) {
    return <RelatedAuditsSkeletons />;
  }

  // Empty state
  if (relatedAudits.length === 0) {
    return (
      <div>
        <p className="text-[11px] uppercase text-muted font-medium tracking-wider mb-2.5">
          Related Audits
        </p>
        <p className="text-[13px] text-muted">
          No audits linked yet. Accept checklist suggestions to link this
          document to audits.
        </p>
      </div>
    );
  }

  // Populated state
  return (
    <div>
      <p className="text-[11px] uppercase text-muted font-medium tracking-wider mb-2.5">
        Related Audits
      </p>
      <div className="space-y-2">
        {relatedAudits.map((audit) => (
          <button
            key={audit.id}
            type="button"
            className="w-full flex items-center gap-3 rounded-xl bg-card/40 border border-border/50 px-4 py-3 hover:bg-card-hover transition-colors group cursor-pointer"
            onClick={() =>
              navigate({
                to: "/$workspaceSlug/audits/$auditId",
                params: {
                  workspaceSlug: workspaceSlug ?? "",
                  auditId: audit.id,
                },
              })
            }
          >
            <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
              <Shield size={16} className="text-blue-400" />
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-[13px] text-primary font-medium truncate">
                {audit.displayName}
              </p>
              <p className="text-[11px] text-muted">{audit.code}</p>
            </div>
            <ChevronRight
              size={14}
              className="text-muted group-hover:text-secondary transition-colors shrink-0"
            />
          </button>
        ))}
      </div>
    </div>
  );
}

function RelatedAuditsSkeletons() {
  return (
    <div>
      <p className="text-[11px] uppercase text-muted font-medium tracking-wider mb-2.5">
        Related Audits
      </p>
      <div className="space-y-2">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="w-full flex items-center gap-3 rounded-xl bg-card/40 border border-border/50 px-4 py-3 animate-pulse"
          >
            <div className="h-8 w-8 rounded-lg bg-muted/20 shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3.5 w-32 bg-muted/20 rounded" />
              <div className="h-2.5 w-20 bg-muted/20 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
